import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import {
  useCreateCustomer,
  useUpdateCustomer,
  type CustomerCreateInput,
  type CustomerDetail,
  type CustomerMaintenanceFrequency,
  type CustomerUpdateInput,
} from '@/hooks/useCustomers';

/**
 * Kunden-Formular (Anlegen + Bearbeiten, Spec S8-06 §5).
 *
 * - slug ist NUR beim Anlegen editierbar (CITEXT-Identität, hängt an
 *   Inventar-Aliasen und Status-Page); auto-slugify aus name solange der
 *   User das Feld nicht selbst angefasst hat.
 * - notification_emails als Zeilen-Textarea (eine Adresse pro Zeile).
 * - maintenance_frequency inkl. on_demand ("Auf Abruf") — Kunden ohne
 *   Vertrag dürfen nie zwangsweise als monatlich fällig gelten (Regel 1).
 */

// Spiegel des Backend-Patterns (schemas/customer.py) für Sofort-Feedback.
const SLUG_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;

// Muss mit VPN_REF_MUSTER (wt-client.py) und dem pattern im Backend-Schema
// übereinstimmen. Ein Kundenstandard trifft ALLE Systeme des Kunden: ein
// Pfad oder Tippfehler lässt nicht ein System, sondern den ganzen Job des
// Kunden auflaufen. Lieber hier rot als nachts im Tunnelaufbau.
/**
 * Erlaubte Form eines VPN-Profils: nur der DATEINAME, kein Pfad.
 *
 * Exportiert, weil das System-Formular denselben Wert in seinem
 * Override-Feld annimmt. Dort fehlte die Prüfung; "/etc/openvpn/kunde.ovpn"
 * ging durch, das Backend nahm es (max_length=200, kein Muster) und der
 * Client lehnte es nachts ab: "VPN-Referenz ... ungültig - kein Kommando
 * ausgeführt" (Befund 10.09.2026).
 */
export const VPN_PROFIL_MUSTER = /^[A-Za-z0-9._ -]{1,80}$/;

/** Der Fehlertext dazu - an Kunde und System wortgleich. */
export const VPN_PROFIL_FEHLER =
  'Nur Buchstaben, Ziffern, Punkt, Unterstrich, Leerzeichen und Bindestrich; höchstens 80 Zeichen. Kein Pfad.';

/** name -> slug mit deutscher Umlaut-Transliteration (ae/oe/ue/ss). */
export function slugifyCustomerName(name: string): string {
  return (
    name
      .trim()
      .toLowerCase()
      // NFC zuerst, damit auch zerlegte Umlaute (a + Trema) getroffen werden.
      .normalize('NFC')
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      // Restliche Diakritika (e-accent etc.) über NFKD-Zerlegung entfernen.
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 120)
      .replace(/-+$/, '')
  );
}

/** Zeilen-Textarea -> bereinigte Mail-Liste (leere Zeilen raus, trim). */
export function parseNotificationEmails(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

const FREQUENCY_OPTIONS: { value: CustomerMaintenanceFrequency; label: string }[] = [
  { value: 'monthly', label: 'Monatlich' },
  { value: 'quarterly', label: 'Quartalsweise' },
  { value: 'biannual', label: 'Halbjährlich' },
  { value: 'yearly', label: 'Jährlich' },
  { value: 'on_demand', label: 'Auf Abruf (kein Zyklus)' },
  { value: 'custom', label: 'Individuell' },
];

interface FormState {
  name: string;
  slug: string;
  slugTouched: boolean;
  legalName: string;
  contactEmail: string;
  contactPhone: string;
  vpnProfile: string;
  maintenanceFrequency: '' | CustomerMaintenanceFrequency;
  notificationEmails: string;
  notes: string;
}

function emptyForm(prefill?: CustomerFormPrefill | null): FormState {
  const name = prefill?.name ?? '';
  return {
    name,
    slug: name ? slugifyCustomerName(name) : '',
    slugTouched: false,
    legalName: '',
    contactEmail: '',
    contactPhone: '',
    vpnProfile: '',
    maintenanceFrequency: '',
    notificationEmails: '',
    notes: '',
  };
}

function formFromCustomer(customer: CustomerDetail): FormState {
  return {
    name: customer.name,
    slug: customer.slug,
    slugTouched: true,
    legalName: customer.legal_name ?? '',
    contactEmail: customer.contact_email ?? '',
    contactPhone: customer.contact_phone ?? '',
    vpnProfile: customer.vpn_profile ?? '',
    maintenanceFrequency: customer.maintenance_frequency ?? '',
    notificationEmails: (customer.notification_emails ?? []).join('\n'),
    notes: customer.notes ?? '',
  };
}

export interface CustomerFormPrefill {
  name?: string;
  /** Für den geführten Flow "Kunde anlegen + verknuepfen" (Vertrags-Wizard). */
  invoice_ninja_client_id?: string;
}

interface CustomerFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Wenn gesetzt: Bearbeiten, sonst Anlegen. */
  customer?: CustomerDetail | null;
  prefill?: CustomerFormPrefill | null;
  onSuccess?: (customer: CustomerDetail) => void;
}

export function CustomerFormDialog({
  open,
  onClose,
  customer,
  prefill,
  onSuccess,
}: CustomerFormDialogProps): JSX.Element {
  const isEdit = Boolean(customer);
  const createMutation = useCreateCustomer();
  const updateMutation = useUpdateCustomer();

  const [form, setForm] = useState<FormState>(() => emptyForm(prefill));

  // Beim Öffnen mit Edit-Werten bzw. Create-Defaults/Prefill befüllen.
  useEffect(() => {
    if (!open) return;
    createMutation.reset();
    updateMutation.reset();
    setForm(customer ? formFromCustomer(customer) : emptyForm(prefill));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customer, prefill]);

  const setName = (name: string): void => {
    setForm((f) => ({
      ...f,
      name,
      // Auto-Slug nur solange der User das Slug-Feld nicht angefasst hat.
      slug: !isEdit && !f.slugTouched ? slugifyCustomerName(name) : f.slug,
    }));
  };

  const slugInvalid =
    !isEdit && form.slug.trim() !== '' && !SLUG_PATTERN.test(form.slug.trim());
  const nameMissing = form.name.trim() === '';
  const vpnProfilInvalid =
    form.vpnProfile.trim() !== '' && !VPN_PROFIL_MUSTER.test(form.vpnProfile.trim());

  const invalidEmails = parseNotificationEmails(form.notificationEmails).filter(
    (e) => !e.includes('@')
  );

  const saving = createMutation.isPending || updateMutation.isPending;
  const error = isEdit ? updateMutation.error : createMutation.error;
  const formInvalid = nameMissing || slugInvalid || invalidEmails.length > 0 || vpnProfilInvalid;

  const submit = (): void => {
    if (formInvalid) return;
    const shared = {
      name: form.name.trim(),
      legal_name: form.legalName.trim() || null,
      contact_email: form.contactEmail.trim() || null,
      contact_phone: form.contactPhone.trim() || null,
      // Leerstring würde in der DB als gesetzt gelten und im Export täuschen.
      vpn_profile: form.vpnProfile.trim() || null,
      notification_emails: parseNotificationEmails(form.notificationEmails),
      notes: form.notes.trim() || null,
      ...(form.maintenanceFrequency
        ? { maintenance_frequency: form.maintenanceFrequency }
        : {}),
    };
    const handleSuccess = (result: CustomerDetail): void => {
      onSuccess?.(result);
      onClose();
    };
    if (isEdit && customer) {
      const body: CustomerUpdateInput = shared;
      updateMutation.mutate(
        { customerId: customer.id, body },
        { onSuccess: handleSuccess }
      );
    } else {
      const body: CustomerCreateInput = {
        ...shared,
        ...(form.slug.trim() ? { slug: form.slug.trim() } : {}),
        ...(prefill?.invoice_ninja_client_id
          ? { invoice_ninja_client_id: prefill.invoice_ninja_client_id }
          : {}),
      };
      createMutation.mutate(body, { onSuccess: handleSuccess });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEdit ? 'Kunde bearbeiten' : 'Neuer Kunde'}
      description={
        isEdit
          ? 'Stammdaten ändern. Der Slug ist fixiert (Inventar-Alias-Anker).'
          : 'Kunde manuell anlegen — voll wartbar, mit oder ohne Vertrag.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            onClick={submit}
            loading={saving}
            disabled={formInvalid}
            data-testid="customer-save"
          >
            Speichern
          </Button>
        </>
      }
    >
      <div className="flex max-h-[65vh] flex-col gap-3 overflow-y-auto pr-1">
        <Input
          label="Name"
          value={form.name}
          onChange={(e) => setName(e.target.value)}
          placeholder="z.B. Sulzer GmbH"
          data-testid="customer-name"
        />
        <Input
          label="Slug"
          value={form.slug}
          onChange={(e) =>
            setForm((f) => ({ ...f, slug: e.target.value, slugTouched: true }))
          }
          disabled={isEdit}
          error={
            slugInvalid
              ? 'Nur Kleinbuchstaben, Ziffern, - und _ (Beginn: Buchstabe/Ziffer).'
              : undefined
          }
          hint={
            isEdit
              ? 'Nach dem Anlegen nicht mehr änderbar.'
              : 'Wird automatisch aus dem Namen erzeugt (ae/oe/ue/ss).'
          }
          className="font-mono"
          data-testid="customer-slug"
        />
        <Input
          label="Firmenname (legal)"
          value={form.legalName}
          onChange={(e) => setForm((f) => ({ ...f, legalName: e.target.value }))}
          placeholder="Offizieller Firmenwortlaut"
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            label="Kontakt-E-Mail"
            type="email"
            value={form.contactEmail}
            onChange={(e) => setForm((f) => ({ ...f, contactEmail: e.target.value }))}
          />
          <Input
            label="Kontakt-Telefon"
            value={form.contactPhone}
            onChange={(e) => setForm((f) => ({ ...f, contactPhone: e.target.value }))}
          />
        </div>
        <Input
          label="VPN-Profil (Standard)"
          value={form.vpnProfile}
          onChange={(e) => setForm((f) => ({ ...f, vpnProfile: e.target.value }))}
          placeholder="kunde.ovpn"
          className="font-mono"
          hint="Nur der Dateiname. Gilt für alle Systeme des Kunden, die keinen eigenen Override haben - der Client baut dann bei jedem Lauf einen Tunnel für diese Systeme auf."
          error={vpnProfilInvalid ? VPN_PROFIL_FEHLER : undefined}
          data-testid="customer-vpn-profil"
        />
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Wartungs-Frequenz</span>
          <select
            value={form.maintenanceFrequency}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                maintenanceFrequency: e.target.value as FormState['maintenanceFrequency'],
              }))
            }
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            data-testid="customer-frequency"
          >
            <option value="">– Standard (monatlich) –</option>
            {FREQUENCY_OPTIONS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Wartungs-Mail-Empfänger</span>
          <textarea
            value={form.notificationEmails}
            onChange={(e) =>
              setForm((f) => ({ ...f, notificationEmails: e.target.value }))
            }
            rows={3}
            placeholder={'office@kunde.at\nit@kunde.at'}
            className="rounded-md border border-border bg-surface px-3 py-2 font-mono text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            data-testid="customer-emails"
          />
          {invalidEmails.length > 0 ? (
            <span className="text-xs text-danger" role="alert">
              Keine gueltige Adresse: {invalidEmails.join(', ')}
            </span>
          ) : (
            <span className="text-xs text-muted">Eine Adresse pro Zeile.</span>
          )}
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Notizen</span>
          <textarea
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            rows={3}
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          />
        </label>
        {error && <ErrorAlert error={error} />}
      </div>
    </Dialog>
  );
}
